import { useState, useEffect, useRef } from 'react';
import { useMemoizedFn, useUnmountedRef } from 'ahooks';

export type UsePollingOptions<T> = {
  /**
   * 每次轮询执行的异步函数。
   * - 返回 `T` 表示本次取到数据（后续由 `isComplete` 判断是否停止）。
   * - 抛出异常视为本次请求出错，连续出错超限时停止轮询并触发 `onError`。
   */
  fetchFn: () => Promise<T>;
  /**
   * 判断是否已完成的函数。
   * 入参为 `fetchFn` 返回值，返回 `true` 时停止轮询并触发 `onComplete`。
   */
  isComplete: (data: T) => boolean;
  /**
   * 完成回调，接收最后一次 `fetchFn` 的返回值。
   */
  onComplete: (data: T) => void;
  /**
   * 错误回调（连续错误超限或超时时触发）。
   */
  onError?: (message: string) => void;
  /**
   * 轮询间隔（毫秒），默认 2000。
   */
  intervalMs?: number;
  /**
   * 最大轮询次数，超出后视为超时，默认 150（≈5 分钟 @ 2s）。
   */
  maxCount?: number;
  /**
   * 最大连续错误次数，超出后停止轮询，默认 3。
   */
  maxConsecutiveErrors?: number;
  /**
   * 超时时的错误提示文案。
   */
  timeoutMessage?: string;
  /**
   * 连续错误超限时的错误提示文案。
   */
  errorMessage?: string;
};

export type UsePollingReturn = {
  /** 是否正在轮询 */
  isPolling: boolean;
  /** 手动启动轮询。防重入：若已在轮询中则直接返回 */
  startPolling: () => void;
  /** 手动停止轮询并重置所有计数 */
  stopPolling: () => void;
};

/**
 * usePolling
 *
 * 通用轮询 Hook，封装定时器管理、计数、防重入、连续错误重试与超时处理。
 * 业务方只需传入 `fetchFn`（异步请求）和 `isComplete`（完成判断）即可复用。
 *
 * @example
 * ```ts
 * const { isPolling, startPolling, stopPolling } = usePolling({
 *   fetchFn: () => someStatusGet({ id }),
 *   isComplete: data => data?.status === 1,
 *   onComplete: data => handleDone(data),
 *   onError: msg => toast.error(msg),
 * });
 * ```
 */
export function usePolling<T>({
  fetchFn,
  isComplete,
  onComplete,
  onError,
  intervalMs = 2000,
  maxCount = 150,
  maxConsecutiveErrors = 3,
  timeoutMessage = 'Request timed out. Please try again.',
  errorMessage = 'Request failed after multiple retries. Please try again.',
}: UsePollingOptions<T>): UsePollingReturn {
  const [isPolling, setIsPolling] = useState(false);
  const unmountedRef = useUnmountedRef();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCountRef = useRef(0);
  const consecutiveErrorCountRef = useRef(0);
  /**
   * 独立运行标志：与 timerRef 解耦，专用于防重入判断。
   * stopPolling 调用后置为 false，但正在执行的 tick 会在 await 返回后
   * 检查该标志，从而安全地提前退出，不再调度下一次。
   */
  const isRunningRef = useRef(false);

  /** 停止并清理当前轮询，重置所有计数 */
  const stopPolling = useMemoizedFn(() => {
    isRunningRef.current = false;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pollCountRef.current = 0;
    consecutiveErrorCountRef.current = 0;
    setIsPolling(false);
  });

  /** 单次轮询执行体（请求完成后递归调度下一次） */
  const tick = useMemoizedFn(async () => {
    // 超出最大轮询次数：超时
    if (pollCountRef.current >= maxCount) {
      stopPolling();
      onError?.(timeoutMessage);
      return;
    }

    pollCountRef.current += 1;

    try {
      const data = await fetchFn();
      // 组件已卸载或 stopPolling 已被调用（await 期间竞态），直接退出
      if (unmountedRef.current || !isRunningRef.current) return;
      if (isComplete(data)) {
        stopPolling();
        onComplete(data);
      } else {
        // 尚未完成，重置连续错误计数，调度下一次
        consecutiveErrorCountRef.current = 0;
        timerRef.current = setTimeout(tick, intervalMs);
      }
    } catch {
      // 组件已卸载或 stopPolling 已被调用（await 期间竞态），直接退出
      if (unmountedRef.current || !isRunningRef.current) return;
      consecutiveErrorCountRef.current += 1;
      if (consecutiveErrorCountRef.current >= maxConsecutiveErrors) {
        stopPolling();
        onError?.(errorMessage);
      } else {
        // 错误未超限，继续调度
        timerRef.current = setTimeout(tick, intervalMs);
      }
    }
  });

  /** 启动轮询。防重入：若 isRunningRef 已为 true 则直接返回 */
  const startPolling = useMemoizedFn(() => {
    if (isRunningRef.current) return;

    isRunningRef.current = true;
    setIsPolling(true);
    pollCountRef.current = 0;
    consecutiveErrorCountRef.current = 0;

    // 立即触发第一次，后续由 tick 内部递归调度
    timerRef.current = setTimeout(tick, 0);
  });

  // 组件卸载时清理定时器，防止内存泄漏
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return { isPolling, startPolling, stopPolling };
}
